// src/firebase/dashboard.service.js
// ────────────────────────────────────────────────────────────
// Service layer for dashboard metrics.
// All Firestore queries are scoped to the authenticated user.
// ────────────────────────────────────────────────────────────
import {
    collection,
    query,
    where,
    getDocs,
    Timestamp,
} from 'firebase/firestore';
import { db } from './firebase.config';

// ============================================================
// 1. PIPELINE VALUE
// ============================================================

/**
 * Calculate the total pipeline value for a user.
 * Sum of `price` across all properties owned by the user.
 * Skips non-numeric / zero / negative prices.
 *
 * @param {string} uid - Authenticated user's UID
 * @returns {Promise<{ total: number, count: number }>}
 */
export const getPipelineValue = async (uid) => {
    if (!uid) throw new Error('uid is required');

    const propertiesRef = collection(db, 'properties');
    const q = query(propertiesRef, where('userId', '==', uid));
    const snapshot = await getDocs(q);

    let total = 0;
    let count = 0;

    snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        let price = data.price;

        // Coerce string prices to number
        if (typeof price === 'string') {
            price = parseFloat(price.replace(/[^0-9.-]/g, ''));
        }

        if (typeof price === 'number' && !isNaN(price) && price > 0) {
            total += price;
            count++;
        }
    });

    return { total, count };
};

// ============================================================
// 2. NEW LEADS — TODAY vs YESTERDAY
// ============================================================

/**
 * Return the start-of-day Date for a given offset from today.
 * offset = 0 → today 00:00, offset = -1 → yesterday 00:00
 * Uses the client's local timezone.
 *
 * @param {number} offset - Number of days relative to today
 * @returns {Date}
 */
const startOfDay = (offset = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    d.setHours(0, 0, 0, 0);
    return d;
};

/**
 * Count leads created today and yesterday for a user.
 *
 * @param {string} uid - Authenticated user's UID
 * @returns {Promise<{ today: number, yesterday: number }>}
 */
export const getNewLeadsStats = async (uid) => {
    if (!uid) throw new Error('uid is required');

    const todayStart = Timestamp.fromDate(startOfDay(0));
    const yesterdayStart = Timestamp.fromDate(startOfDay(-1));
    const now = Timestamp.now();

    const leadsRef = collection(db, 'leads');

    // Today: createdAt >= todayStart AND createdAt <= now
    const todayQuery = query(
        leadsRef,
        where('userId', '==', uid),
        where('createdAt', '>=', todayStart),
        where('createdAt', '<=', now),
    );

    // Yesterday: createdAt >= yesterdayStart AND createdAt < todayStart
    const yesterdayQuery = query(
        leadsRef,
        where('userId', '==', uid),
        where('createdAt', '>=', yesterdayStart),
        where('createdAt', '<', todayStart),
    );

    const [todaySnap, yesterdaySnap] = await Promise.all([
        getDocs(todayQuery),
        getDocs(yesterdayQuery),
    ]);

    return {
        today: todaySnap.size,
        yesterday: yesterdaySnap.size,
    };
};

// ============================================================
// 3. ACTION REQUIRED LEADS
// ============================================================

/**
 * Count "action required" leads for a user.
 *
 * A lead is action-required when ALL of:
 *   1. Lead belongs to the user (userId == uid)
 *   2. Lead status is 'Active'
 *   3. The lead's linked conversation has unreadCount > 0
 *
 * Implementation:
 *   - Fetch conversations with unreadCount > 0 for the user.
 *   - Collect the set of leadIds from those conversations.
 *   - Fetch active leads for the user.
 *   - Intersect: active leads whose id is in the unread leadId set.
 *
 * @param {string} uid - Authenticated user's UID
 * @returns {Promise<{ count: number, leadIds: string[] }>}
 */
export const getActionRequiredCount = async (uid) => {
    if (!uid) throw new Error('uid is required');

    // Step 1: Get conversations with unread messages
    const convsRef = collection(db, 'conversations');
    const unreadQuery = query(
        convsRef,
        where('userId', '==', uid),
        where('unreadCount', '>', 0),
    );
    const unreadSnap = await getDocs(unreadQuery);

    // Build set of leadIds that have unread conversations
    const unreadLeadIds = new Set();
    unreadSnap.forEach((docSnap) => {
        const leadId = docSnap.data().leadId;
        if (leadId) unreadLeadIds.add(leadId);
    });

    if (unreadLeadIds.size === 0) {
        return { count: 0, leadIds: [] };
    }

    // Step 2: Get active leads for this user
    const leadsRef = collection(db, 'leads');
    const activeLeadsQuery = query(
        leadsRef,
        where('userId', '==', uid),
        where('status', '==', 'Active'),
    );
    const activeLeadsSnap = await getDocs(activeLeadsQuery);

    // Step 3: Intersect — active leads that have unread conversations
    const actionLeadIds = [];
    activeLeadsSnap.forEach((docSnap) => {
        if (unreadLeadIds.has(docSnap.id)) {
            actionLeadIds.push(docSnap.id);
        }
    });

    return { count: actionLeadIds.length, leadIds: actionLeadIds };
};
